import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useInvertedCursor } from '../../hooks/useInvertedCursor';
import { BackgroundBeams } from '../../components/ui/background-beams';
import { SEO } from '../../components/SEO';
import { MapPinIcon, CheckBadgeIcon, ChartBarIcon, DevicePhoneMobileIcon } from '../../components/Icons';
import './NexaSync.css';

const FadeInSection = ({ children, className = '', delay = '' }) => {
    const [ref, isVisible] = useIntersectionObserver({ triggerOnce: true, threshold: 0.1 });
    return (
        <div ref={ref} className={`fade-in-section ${isVisible ? 'is-visible' : ''} ${className} ${delay}`}>
            {children}
        </div>
    );
};

const NexaSync = () => {
    useInvertedCursor();
    
    useEffect(() => {
        // Add structured data for NexaSync page
        const softwareSchema = {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "NexaSync - Field Force Management",
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Web, iOS, Android",
            "offers": {
                "@type": "Offer",
                "url": "https://nexasync.io"
            },
            "provider": {
                "@type": "Organization",
                "name": "Mad Over Technology",
                "url": "https://madovertech.io"
            },
            "description": "Streamline your field operations with intelligent workforce management and real-time tracking. Field Force Management solution for task management, GPS tracking, and analytics."
        };

        let schemaScript = document.querySelector('script[data-schema="nexasync"]');
        if (!schemaScript) {
            schemaScript = document.createElement('script');
            schemaScript.setAttribute('type', 'application/ld+json');
            schemaScript.setAttribute('data-schema', 'nexasync');
            document.head.appendChild(schemaScript);
        }
        schemaScript.textContent = JSON.stringify(softwareSchema);
    }, []);

    return (
        <>
            <SEO 
                title="Field Force Management (NexaSync) | Mad Over Technology"
                description="Streamline your field operations with intelligent workforce management and real-time tracking. NexaSync offers task management, GPS tracking, analytics, and mobile app for field workers."
                keywords="Field Force Management, NexaSync, Workforce Management, Field Tracking, Task Management, GPS Tracking, Field Operations, Mobile Workforce, India"
                url="/nexasync"
                image="/nexasync/nexasync-dashboard.png"
            />
        <main className="nexasync relative">
            {/* Hero Section */}
            <section className="hero-section relative overflow-hidden flex items-center">
                <BackgroundBeams className="z-0" />
                <div className="container mx-auto px-4 relative z-10 grid lg:grid-cols-2 gap-12 items-center">
                    <div className="hero-text text-left">
                        <h1 className="hero-title text-5xl md:text-7xl font-bold mb-6 text-neutral-900 dark:text-black">
                            Field Force Management
                        </h1>
                        <p className="hero-subtitle text-lg md:text-xl text-neutral-600 dark:text-neutral-400 mb-8 max-w-lg mx-0">
                            Streamline your field operations with intelligent workforce management and real-time tracking.
                        </p>
                        <div className="flex gap-4 justify-start flex-wrap">
                            <a href="https://nexasync.io" target="_blank" rel="noopener noreferrer" className="cta-button">
                                <svg className="demo-icon" width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                                    <path d="M18 19H6c-.55 0-1-.45-1-1V6c0-.55.45-1 1-1h5c.55 0 1-.45 1-1s-.45-1-1-1H5c-1.11 0-2 .89-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-6c0-.55-.45-1-1-1s-1 .45-1 1v5c0 .55-.45 1-1 1zM14 4c0 .55.45 1 1 1h2.59l-9.13 9.13c-.39.39-.39 1.02 0 1.41.39.39 1.02.39 1.41 0L19 6.41V9c0 .55.45 1 1 1s1-.45 1-1V4c0-.55-.45-1-1-1h-5c-.55 0-1 .45-1 1z"/>
                                </svg>
                                <span>Visit NexaSync</span>
                            </a>
                            <a href="https://wa.me/919711699861?text=Hi%2C%20I%27m%20interested%20in%20NexaSync%20Field%20Force%20Management" target="_blank" rel="noopener noreferrer" className="cta-button whatsapp-button">
                                <svg className="whatsapp-icon" width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                                </svg>
                                <span>WhatsApp Us</span>
                            </a>
                        </div>
                    </div>
                    <div className="hero-visual">
                        <img 
                            src="/nexasync/nexasync-dashboard.png" 
                            alt="NexaSync Field Force Management Dashboard - Real-time tracking, attendance summary, and workforce analytics"
                            className="w-full rounded-2xl shadow-2xl border border-neutral-200 dark:border-neutral-800"
                            loading="eager"
                        />
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="features-section">
                <FadeInSection>
                    <h2 className="section-title">Key Features</h2>
                </FadeInSection>
                <div className="steps-grid">
                    {[
                        { title: 'Real-time Tracking', desc: 'Monitor your field force in real-time with GPS tracking and location updates.', Icon: MapPinIcon },
                        { title: 'Track Client Visits', desc: 'Efficiently track and manage client visits with detailed visit logs and history.', Icon: CheckBadgeIcon },
                        { title: 'Analytics & Reports', desc: 'Get detailed insights and reports on field operations and performance.', Icon: ChartBarIcon },
                        { title: 'Mobile App', desc: 'Full-featured mobile application for field workers and managers.', Icon: DevicePhoneMobileIcon }
                    ].map((feature, index) => (
                        <FadeInSection key={feature.title} className="step-card" delay={`stagger-${index + 1}`}>
                            <div className="step-number">0{index + 1}</div>
                            <div className="step-content">
                                <div style={{ marginBottom: '1rem' }}><feature.Icon className="w-10 h-10" /></div>
                                <h3 className="step-title">{feature.title}</h3>
                                <p>{feature.desc}</p>
                            </div>
                        </FadeInSection>
                    ))}
                </div>
            </section>

            {/* CTA Section */}
            <section id="contact" className="cta-section">
                <FadeInSection className="cta-content">
                    <h2 className="cta-title">Ready to Transform Your Field Operations?</h2>
                    <p className="cta-subtitle">Discover how NexaSync can streamline your workforce management.</p>
                    <div className="cta-buttons">
                        <a href="https://nexasync.io" target="_blank" rel="noopener noreferrer" className="cta-button">Visit NexaSync</a>
                        <a href="https://wa.me/919711699861?text=Hi%2C%20I%27m%20interested%20in%20NexaSync%20Field%20Force%20Management" target="_blank" rel="noopener noreferrer" className="cta-button whatsapp-button">
                            <svg className="whatsapp-icon" width="20" height="20" viewBox="0 0 24 24" fill="#ffffff">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                            </svg>
                            <span>WhatsApp Us</span>
                        </a>
                    </div>
                </FadeInSection>
            </section>

            {/* Footer */}
            <footer className="footer">
                <div className="footer-links">
                    <Link to="/">Home</Link>
                    <Link to="/ai-photobooth">AI Photo Booth</Link>
                    <a href="https://nexasync.io" target="_blank" rel="noopener noreferrer">NexaSync</a>
                    <Link to="/terms-of-service">Terms of Service</Link>
                    <Link to="/privacy-policy">Privacy Policy</Link>
                    <Link to="/refund-policy">Refund Policy</Link>
                    <a href="https://www.instagram.com/madovertechnology" target="_blank" rel="noopener noreferrer">Instagram</a>
                    <a href="https://wa.me/919711699861?text=Hi%2C%20I%27m%20interested%20in%20learning%20more%20about%20your%20services" target="_blank" rel="noopener noreferrer">Contact</a>
                </div>
                <div className="footer-locations">
                    <span>DELHI | BENGALORE | MUMBAI</span>
                </div>
                <div className="footer-contact">
                    <a href="mailto:hello@madovertech.io" className="footer-email">hello@madovertech.io</a>
                    <span className="footer-separator">|</span>
                    <a href="tel:+919711689861" className="footer-phone">+91 9711689861</a>
                </div>
                <p className="footer-copyright">© 2025 Mad Over Technology. All rights reserved.</p>
            </footer>
        </main>
        </>
    );
};

export default NexaSync;

